// @ts-strict-ignore
import { GetStaticProps } from 'next';
import { useIntl } from 'react-intl';

import { TopicListDocument, TopicListItem } from 'api';
import { initializeApollo } from 'lib/graphql/client';
import { createLogger } from 'lib/logging';

import { LazyLoadAd, TopicPageAds } from 'components/ads';
import ContentContainer from 'components/content-container';
import { Layout } from 'components/layout';
import PageHeader from 'components/page-header';
import TopicsGroup from 'components/pages/topics/topics-group';
import { useIsBreakpointWidth } from 'lib/hooks/useIsBreakpointWidth';
import Divider from '../../components/divider';

const HASH = '#';

export type TopicsGroupData = {
  letter: string;
  items: Array<{
    name: string;
    slug: string;
  }>;
};

type TopicsPageProps = {
  list: Array<TopicsGroupData>;
};

const Topics = ({ list }: TopicsPageProps) => {
  const { formatMessage } = useIntl();
  const firstTwoItemsList = list.slice(0, 2);
  const restOfList = list.slice(2);
  const isMobile = useIsBreakpointWidth({ size: 'sm', breakPointType: 'ads' });
  const adClass = isMobile ? 'flex flex-col-reverse' : 'flex flex-row';
  return (
    <Layout>
      <TopicPageAds.TopicsAdBillBoardATF />
      <div className="w-full bg-white">
        <PageHeader
          title={formatMessage({
            id: 'topics.title',
            defaultMessage: 'Topics'
          })}
          subtitle={formatMessage({
            id: 'topics.subtitle',
            defaultMessage: 'Browse TED topics from A to Z'
          })}
        />
        <ContentContainer className="xl:pb-9 xl:pt-8">
          <div className={adClass}>
            <div>
              {firstTwoItemsList?.map((group, index) => {
                const isLastGroup = index === firstTwoItemsList?.length - 1;
                return (
                  <TopicsGroup
                    key={group.letter}
                    data={group}
                    isLastGroup={isLastGroup}
                    nextGroupLetter={
                      isLastGroup
                        ? undefined
                        : firstTwoItemsList[index + 1].letter
                    }
                  />
                );
              })}
            </div>
            <LazyLoadAd>
              <TopicPageAds.TopicsAdInlineATF />
            </LazyLoadAd>
          </div>
          <Divider isLarge />
          <LazyLoadAd>
            <TopicPageAds.TopicsAdInlineBTF />
          </LazyLoadAd>
          {restOfList?.map((group, index) => {
            const isLastGroup = index === restOfList?.length - 1;
            return (
              <TopicsGroup
                key={group.letter}
                data={group}
                isLastGroup={isLastGroup}
                nextGroupLetter={
                  isLastGroup ? undefined : restOfList[index + 1].letter
                }
              />
            );
          })}
        </ContentContainer>
      </div>
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const apolloClient = initializeApollo();
  const { data, error } = await apolloClient.query({
    query: TopicListDocument
  });

  // Logging errors, but data fetching will silently fail and return empty set
  if (error) {
    const log = createLogger('Topics');
    log.error('getting topics list failed', error);
  }

  // sort topic list alphabetically by name
  const sorted: TopicListItem[] = [...data.topicList.nodes].sort(
    (a: TopicListItem, b: TopicListItem) =>
      (a.name?.toUpperCase() || '') > (b.name?.toUpperCase() || '') ? 1 : -1
  );

  // group list by starting letter
  const repacked = sorted.reduce(
    (acc: { list: TopicsGroupData[] }, item: TopicListItem) => {
      const firstLetter = (item.name?.[0] || '').toUpperCase();
      const isLetter = firstLetter.toLowerCase() !== firstLetter.toUpperCase();

      if (
        acc.list[acc.list.length - 1]?.letter.toUpperCase() !==
        (isLetter ? firstLetter : HASH)
      )
        acc.list.push({ letter: isLetter ? firstLetter : HASH, items: [] });

      if (item.name && item.slug) {
        acc.list[acc.list.length - 1].items.push({
          name: item.name,
          slug: item.slug
        });
      }

      return acc;
    },
    { list: [] }
  );

  // move "#" group to end of the list
  if (repacked.list[0]?.letter === HASH) {
    const hashGroup = repacked.list.shift();
    if (hashGroup) {
      repacked.list.push(hashGroup);
    }
  }

  return {
    props: {
      list: repacked.list || []
    },
    revalidate: 180
  };
};

export default Topics;
